import { useEffect, useMemo, useState } from "react";
import { Button, Skeleton, Typography } from "antd";
import { ArrowRightOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { useNavigate } from "react-router-dom";
import { markdownComponents } from "../components/MarkdownLink";
import { generateDailySummary, todayKey } from "../lib/api";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import {
  BAND_COLOR,
  BAND_DOT_CLASS,
  ON_HOLD_STATUSES,
  REJECTED_STATUSES,
  WORKFLOW_STATUSES,
  bandFor,
  type Band,
} from "../lib/projectStatus";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";

interface Project {
  id: string;
  name: string;
  status: string;
  type?: string;
  createdAt?: string;
  endDate?: string;
}

interface SummaryMetrics {
  mainLabel: string;
  mainValue: string | number;
  detailLabel: string;
  detailValue: string;
}

interface DailySummaryDoc {
  date?: string;
  markdown?: string;
  items?: string[];
  metrics?: SummaryMetrics;
  generatedAt?: string;
  model?: string;
  requestedBy?: string;
}

function formatToday(): string {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const today = useMemo(() => todayKey(), []);
  const [summaryDoc, setSummaryDoc] = useState<DailySummaryDoc | null>(null);
  const [summaryReady, setSummaryReady] = useState(false);
  const [streamingMarkdown, setStreamingMarkdown] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectCount, setProjectCount] = useState(0);
  const [vendorCount, setVendorCount] = useState(0);
  const [intakeCount, setIntakeCount] = useState(0);

  useEffect(() => {
    const ref = doc(db, "aiSummaries", today);
    const unsubscribe = onSnapshot(ref, (snapshot) => {
      setSummaryReady(true);
      if (snapshot.exists()) {
        setSummaryDoc(snapshot.data() as DailySummaryDoc);
      } else {
        setSummaryDoc(null);
      }
    });
    return () => unsubscribe();
  }, [today]);

  useEffect(() => {
    const projectsQuery = query(collection(db, "projects"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(projectsQuery, (snapshot) => {
      const data: Project[] = [];
      snapshot.forEach((d) => data.push({ ...(d.data() as Project), id: d.id }));
      setProjects(data);
      setProjectCount(snapshot.size);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const vendorsQuery = query(
      collection(db, "vendorsPublic"),
      where("status", "==", "active")
    );
    const unsubscribe = onSnapshot(vendorsQuery, (snapshot) => {
      setVendorCount(snapshot.size);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // New intakes pending review (status="Submitted" — the entry-point bucket).
    const intakesQuery = query(
      collection(db, "publicIntakes"),
      where("status", "==", "Submitted")
    );
    const unsubscribe = onSnapshot(intakesQuery, (snapshot) => {
      setIntakeCount(snapshot.size);
    });
    return () => unsubscribe();
  }, []);

  const handleRefreshSummary = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setStreamingMarkdown("");
    try {
      const userName = user?.displayName || user?.email || "User";
      const userEmail = user?.email || "";
      await generateDailySummary(
        {
          onDelta: (chunk) => setStreamingMarkdown((prev) => `${prev}${chunk}`),
          onMeta: () => {
            // onSnapshot will pick up the new persisted doc.
          },
        },
        { userName, userEmail, timestamp: new Date().toISOString() }
      );
    } catch (error) {
      console.error("Failed to refresh daily summary:", error);
    } finally {
      setRefreshing(false);
      setStreamingMarkdown("");
    }
  };

  const summaryIsToday = summaryDoc?.date === today;
  const summaryMarkdown = refreshing
    ? streamingMarkdown
    : summaryIsToday
      ? summaryDoc?.markdown ?? ""
      : "";
  const hasMarkdown = Boolean(summaryMarkdown && summaryMarkdown.trim().length > 0);

  const activeProjects = projects.filter(
    (p) => bandFor(p.status || "Unknown") === "active"
  ).length;
  const shippedProjects = projects.filter(
    (p) => bandFor(p.status || "Unknown") === "shipped"
  ).length;
  const pausedProjects = projects.filter(
    (p) => bandFor(p.status || "Unknown") === "paused"
  ).length;
  const rejectedProjects = projects.filter(
    (p) => bandFor(p.status || "Unknown") === "rejected"
  ).length;

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    projects.forEach((p) => {
      const status = p.status || "Unknown";
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [projects]);

  const activeProjectRows = useMemo(() => {
    return projects
      .filter((p) => bandFor(p.status || "Unknown") === "active")
      .slice(0, 6);
  }, [projects]);

  const showGenerateCta = summaryReady && !summaryIsToday && !refreshing;
  const isBusy = !summaryReady || refreshing;

  const refreshLabel = summaryIsToday ? "Refresh" : "Generate";
  const RefreshIcon = summaryIsToday ? ReloadOutlined : PlusOutlined;

  return (
    <div className="apex-page">
      {/* Page header */}
      <div className="apex-page-header">
        <div>
          <p className="apex-eyebrow">Operations &middot; {formatToday()}</p>
          <h1 className="apex-page-title" style={{ marginTop: 6 }}>
            {(() => {
              const hour = new Date().getHours();
              const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
              const name = user?.displayName?.split(" ")[0] || user?.email?.split("@")[0] || "there";
              return `${greeting}, ${name}.`;
            })()}
          </h1>
          <p className="apex-page-subtitle">
            Here&rsquo;s the OAIP pipeline at a glance.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button
            type="text"
            icon={<RefreshIcon spin={refreshing && summaryIsToday} />}
            loading={refreshing}
            onClick={handleRefreshSummary}
            disabled={isBusy}
          >
            {refreshing ? "Working..." : `${refreshLabel} briefing`}
          </Button>
        </div>
      </div>

      {/* Hero command bar — brand-stripe accented surface with the four
          headline metrics inline. Replaces the 3 stacked cards. */}
      <section className="apex-hero" aria-label="Today's pipeline">
        <div className="apex-hero__lead">
          <p className="apex-eyebrow">Today&rsquo;s pipeline</p>
          <h2
            className="apex-page-title"
            style={{ marginTop: 8, fontSize: 22 }}
          >
            {intakeCount > 0
              ? `${intakeCount} ${intakeCount === 1 ? "intake awaits" : "intakes await"} review`
              : "All intakes triaged"}
          </h2>
          <p className="apex-page-subtitle" style={{ marginTop: 6 }}>
            {activeProjects} active &middot; {shippedProjects} delivered &middot; {pausedProjects + rejectedProjects} closed
          </p>
        </div>
        <div className="apex-hero__metrics">
          <div className="apex-metric">
            <span className="apex-metric__label">Pending intakes</span>
            <span className="apex-metric__value">{intakeCount}</span>
            <span className="apex-metric__detail">awaiting first review</span>
          </div>
          <div className="apex-metric-divider" />
          <div className="apex-metric">
            <span className="apex-metric__label">Active projects</span>
            <span className="apex-metric__value">{activeProjects}</span>
            <span className="apex-metric__detail">{projectCount} total in portfolio</span>
          </div>
          <div className="apex-metric-divider" />
          <div className="apex-metric">
            <span className="apex-metric__label">Vendor partners</span>
            <span className="apex-metric__value">{vendorCount}</span>
            <span className="apex-metric__detail">with active agreements</span>
          </div>
        </div>
      </section>

      {/* Two-column body: briefing on the left, status breakdown on the
          right. Stacks on narrow screens. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
          gap: 20,
        }}
        className="apex-dashboard-body"
      >
        {/* Briefing */}
        <section
          className="apex-surface"
          style={{ padding: 24, display: "flex", flexDirection: "column" }}
          aria-busy={isBusy}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <div>
              <p className="apex-eyebrow">Daily briefing</p>
              <h3 className="apex-section-title" style={{ marginTop: 4 }}>
                Today&rsquo;s summary
              </h3>
            </div>
          </div>

          {!summaryReady && (
            <Skeleton active title={false} paragraph={{ rows: 6 }} />
          )}

          {hasMarkdown && (
            <div
              className="apex-briefing"
              aria-live={refreshing ? "polite" : "off"}
              aria-busy={refreshing}
              style={{
                paddingLeft: refreshing ? 12 : 0,
                borderLeft: refreshing
                  ? "2px solid #1E79C8"
                  : "2px solid transparent",
                transition: "padding 200ms ease, border-color 200ms ease",
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
                {summaryMarkdown}
              </ReactMarkdown>
              {refreshing && (
                <Typography.Text type="secondary" aria-hidden style={{ fontSize: 12 }}>
                  Streaming&hellip;
                </Typography.Text>
              )}
            </div>
          )}

          {!hasMarkdown && summaryReady && !refreshing && (
            <div
              style={{
                color: "var(--apex-ink-muted)",
                fontSize: 14,
                padding: "24px 0",
              }}
            >
              {showGenerateCta
                ? "No summary yet for today. Generate one above."
                : "Loading today's briefing..."}
            </div>
          )}

          {summaryIsToday && (summaryDoc?.generatedAt || summaryDoc?.model) && hasMarkdown && (
            <div
              style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: "1px solid var(--apex-divider)",
                fontSize: 12,
                color: "var(--apex-ink-subtle)",
              }}
            >
              {summaryDoc?.generatedAt && (
                <span>
                  Generated{" "}
                  {new Date(summaryDoc.generatedAt).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
              )}
              {summaryDoc?.generatedAt && summaryDoc?.model && " · "}
              {summaryDoc?.model && <span>{summaryDoc.model}</span>}
            </div>
          )}
        </section>

        {/* Status breakdown */}
        <section
          className="apex-surface"
          style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}
        >
          <div>
            <p className="apex-eyebrow">Pipeline distribution</p>
            <h3 className="apex-section-title" style={{ marginTop: 4 }}>
              Project status
            </h3>
          </div>

          {projectCount === 0 ? (
            <div style={{ color: "var(--apex-ink-muted)", fontSize: 14 }}>
              No projects yet.
            </div>
          ) : (
            <>
              <div
                className="apex-stack-bar"
                role="img"
                aria-label={`${activeProjects} active, ${shippedProjects} delivered, ${pausedProjects} paused, ${rejectedProjects} rejected`}
              >
                {([
                  ["active", activeProjects],
                  ["shipped", shippedProjects],
                  ["paused", pausedProjects],
                  ["rejected", rejectedProjects],
                ] as Array<[Band, number]>)
                  .filter(([, n]) => n > 0)
                  .map(([band, n]) => (
                    <div
                      key={band}
                      className="apex-stack-bar__seg"
                      style={{ flex: n, background: BAND_COLOR[band] }}
                      title={`${band === "shipped" ? "delivered" : band}: ${n}`}
                    />
                  ))}
              </div>

              {/* Per-status rows, grouped */}
              <div>
                {[...WORKFLOW_STATUSES, ...ON_HOLD_STATUSES, ...REJECTED_STATUSES]
                  .map((status) => ({ status, count: statusCounts[status] || 0 }))
                  .filter(({ count }) => count > 0)
                  .map(({ status, count }) => {
                    const band = bandFor(status);
                    return (
                      <div className="apex-status-row" key={status}>
                        <span className="apex-status-row__label">
                          <span className={`apex-dot ${BAND_DOT_CLASS[band]}`} />
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {status}
                          </span>
                        </span>
                        <span className="apex-status-row__count">{count}</span>
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </section>
      </div>

      {/* Active projects strip */}
      <section className="apex-surface" style={{ padding: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px",
            borderBottom: "1px solid var(--apex-hairline)",
          }}
        >
          <div>
            <p className="apex-eyebrow">In motion</p>
            <h3 className="apex-section-title" style={{ marginTop: 4 }}>
              Active projects
            </h3>
          </div>
          <Button
            type="text"
            onClick={() => navigate("/projects")}
            icon={<ArrowRightOutlined />}
            iconPosition="end"
          >
            View all
          </Button>
        </div>

        {activeProjectRows.length === 0 ? (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "var(--apex-ink-muted)",
              fontSize: 14,
            }}
          >
            No active projects. New projects come from agency or vendor intakes.
          </div>
        ) : (
          <div>
            {activeProjectRows.map((project) => {
              const due = project.endDate
                ? new Date(project.endDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                : "No deadline";
              const days = project.endDate
                ? Math.floor(
                    (new Date(project.endDate).getTime() - Date.now()) /
                      (1000 * 60 * 60 * 24)
                  )
                : null;
              const dueClass =
                days === null
                  ? "apex-dot--neutral"
                  : days < 0
                    ? "apex-dot--red"
                    : days < 7
                      ? "apex-dot--red"
                      : days < 14
                        ? "apex-dot--yellow"
                        : "apex-dot--green";
              return (
                <div
                  key={project.id}
                  className="apex-project-row"
                  onClick={() => navigate("/projects")}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate("/projects");
                    }
                  }}
                >
                  <span className="apex-project-row__name">{project.name}</span>
                  <span className="apex-project-row__meta">{project.status}</span>
                  <span
                    className="apex-project-row__meta"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    <span className={`apex-dot ${dueClass}`} />
                    {due}
                  </span>
                  <ArrowRightOutlined
                    style={{ color: "var(--apex-ink-subtle)", fontSize: 12 }}
                    aria-hidden
                  />
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Responsive override — stack the two-col body below 960px. */}
      <style>{`
        @media (max-width: 960px) {
          .apex-dashboard-body {
            grid-template-columns: 1fr !important;
          }
          .apex-hero__metrics {
            flex-wrap: wrap;
            gap: 20px !important;
          }
        }
      `}</style>
    </div>
  );
}
